type ModulTyp =
  | 'lesebestaetigung'
  | 'umfrage'
  | 'texteingabe'
  | 'dateiupload'
  | 'lernbuerostatistik'
  | 'dateianhang'
  | 'terminbuchen'
  | 'zahlung'
  | 'elternportal_einladung';

export interface ExportModule {
  id: number;
  modul_typ: ModulTyp;
  titel?: string | null;
  position?: number;
  konfiguration?: any;
  fragen?: Array<{
    id: number;
    frage_text: string;
  }>;
}

export interface ExportResponse {
  modul_id: number;
  modul_typ: ModulTyp;
  modul_titel: string;
  antwort_data?: any;
  antwort_text?: string | null;
  beantwortet_am?: string | null;
}

export interface ExportRecipient {
  id: number;
  empfaenger_name?: string;
  empfaenger_email: string;
  Vorname: string;
  Name: string;
  Klasse: string;
  geoeffnet_am?: string | null;
  beantwortet_am?: string | null;
  antwort_status?: string;
  antworten?: ExportResponse[];
}

export interface AppointmentBooking {
  configId: number;
  moduleTitle?: string;
  start_time: string;
  end_time: string;
  parent_name?: string | null;
  parent_email?: string | null;
  student_name?: string | null;
  booking_status?: string | null;
  notes?: string | null;
}

type ColumnDef =
  | { type: 'lese'; label: string; modulId: number }
  | { type: 'text'; label: string; modulId: number }
  | { type: 'umfrage'; label: string; modulId: number; frageId: number }
  | { type: 'termin'; label: string; configId: number };

const normalizeValue = (value: string | null | undefined) =>
  (value || '').toString().trim().toLowerCase();

const formatDateTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('de-DE');
};

const formatAppointment = (booking: AppointmentBooking) => {
  const start = formatDateTime(booking.start_time);
  const end = formatDateTime(booking.end_time);
  const status = booking.booking_status ? ` (${booking.booking_status})` : '';
  const range = start && end ? `${start}–${end}` : start || end || '';
  return `${range}${status}`.trim();
};

const createColumns = (modules: ExportModule[]) => {
  const sorted = [...modules].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const columns: ColumnDef[] = [];

  for (const module of sorted) {
    const title = module.titel || module.modul_typ;
    if (module.modul_typ === 'lesebestaetigung') {
      columns.push({ type: 'lese', label: `Lesebestätigung: ${title}`, modulId: module.id });
    }
    if (module.modul_typ === 'texteingabe') {
      columns.push({ type: 'text', label: `Texteingabe: ${title}`, modulId: module.id });
    }
    if (module.modul_typ === 'umfrage' && module.fragen) {
      module.fragen.forEach(frage => {
        columns.push({
          type: 'umfrage',
          label: `Umfrage: ${title} - ${frage.frage_text}`,
          modulId: module.id,
          frageId: frage.id
        });
      });
    }
    if (module.modul_typ === 'terminbuchen') {
      const configId = module.konfiguration?.appointment_config_id;
      if (configId) {
        columns.push({
          type: 'termin',
          label: `Terminbuchung: ${title}`,
          configId
        });
      }
    }
  }

  return columns;
};

const buildBookingIndex = (
  recipients: ExportRecipient[],
  bookings: AppointmentBooking[]
) => {
  const byEmail = new Map<string, number[]>();
  const byStudent = new Map<string, number[]>();

  recipients.forEach(recipient => {
    const emailKey = normalizeValue(recipient.empfaenger_email);
    if (emailKey) {
      const list = byEmail.get(emailKey) || [];
      list.push(recipient.id);
      byEmail.set(emailKey, list);
    }

    const studentKey = normalizeValue(`${recipient.Vorname} ${recipient.Name}`);
    if (studentKey) {
      const list = byStudent.get(studentKey) || [];
      list.push(recipient.id);
      byStudent.set(studentKey, list);
    }
  });

  const result = new Map<number, Map<number, string[]>>();

  bookings.forEach(booking => {
    const matches = new Set<number>();
    const emailKey = normalizeValue(booking.parent_email || '');
    const studentKey = normalizeValue(booking.student_name || '');

    if (emailKey && byEmail.has(emailKey)) {
      byEmail.get(emailKey)!.forEach(id => matches.add(id));
    }
    if (studentKey && byStudent.has(studentKey)) {
      byStudent.get(studentKey)!.forEach(id => matches.add(id));
    }

    if (matches.size === 0) return;

    const formatted = formatAppointment(booking);
    matches.forEach(recipientId => {
      const perRecipient = result.get(recipientId) || new Map<number, string[]>();
      const perConfig = perRecipient.get(booking.configId) || [];
      if (formatted) perConfig.push(formatted);
      perRecipient.set(booking.configId, perConfig);
      result.set(recipientId, perRecipient);
    });
  });

  return result;
};

const safeCsvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

export const buildElternbriefResultsCsv = (
  recipients: ExportRecipient[],
  modules: ExportModule[],
  bookings: AppointmentBooking[]
) => {
  const columns = createColumns(modules);
  const bookingIndex = buildBookingIndex(recipients, bookings);

  const headers = [
    'Schüler',
    'Klasse',
    'Empfänger',
    'E-Mail',
    'Status',
    'Geöffnet am',
    'Beantwortet am',
    ...columns.map(col => col.label)
  ];

  const rows = recipients.map(recipient => {
    const responseMap = new Map<number, ExportResponse>();
    (recipient.antworten || []).forEach(response => {
      responseMap.set(response.modul_id, response);
    });

    const base = [
      `${recipient.Vorname} ${recipient.Name}`.trim(),
      recipient.Klasse || '',
      recipient.empfaenger_name || '',
      recipient.empfaenger_email || '',
      recipient.antwort_status || '',
      formatDateTime(recipient.geoeffnet_am),
      formatDateTime(recipient.beantwortet_am)
    ];

    const extra = columns.map(column => {
      if (column.type === 'termin') {
        const perRecipient = bookingIndex.get(recipient.id);
        const items = perRecipient?.get(column.configId) || [];
        return items.join(' | ');
      }

      const response = responseMap.get(column.modulId);
      if (!response) return '';

      if (column.type === 'lese') {
        const data = response.antwort_data || {};
        if (data.checkbox_bestaetigt) return 'Checkbox bestätigt';
        if (data.link_opened) return 'Link geöffnet';
        return 'Bestätigt';
      }

      if (column.type === 'text') {
        const data = response.antwort_data || {};
        return response.antwort_text || data.text || '';
      }

      if (column.type === 'umfrage') {
        const data = response.antwort_data || {};
        const answers = data.umfrage_antworten || [];
        const entry = answers.find((a: any) => a.frage_id === column.frageId);
        if (!entry) return '';
        if (entry.antwort_text) return entry.antwort_text;
        if (Array.isArray(entry.ausgewaehlte_optionen)) {
          return entry.ausgewaehlte_optionen.join(', ');
        }
        if (entry.bewertung) return String(entry.bewertung);
        return '';
      }

      return '';
    });

    return [...base, ...extra];
  });

  return [
    headers.map(safeCsvCell).join(','),
    ...rows.map(row => row.map(cell => safeCsvCell(cell || '')).join(','))
  ].join('\n');
};

export const downloadCsv = (csvData: string, filename: string) => {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
